import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import dayjs from 'dayjs';
import { NotamAssessmentFieldsFragment, NotamInfoFieldsFragment, NotamSeverity } from 'src/app/core/graphql/generated/gen-types';
import { PipeModule } from 'src/app/shared/pipes.module';

const COMPASS_POINTS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];

/**
 * Shared presentational NOTAM list (095, FR-006/007/008/010).
 *
 * Modes:
 * - `assessment` — full list: severity-sorted NOTAMs, collapsed wide-area group,
 *   explicit stale/noData/empty states, freshness + PIC disclaimer footer.
 * - `single` — compact single-NOTAM mode for map info displays (no footer/states).
 *
 * The "connect your Autorouter account" state is NOT handled here: parents derive it
 * from NotamService.connectionState$ and render their own quiet hint instead.
 */
@Component({
	selector: 'app-notam-list',
	standalone: true,
	imports: [CommonModule, TranslateModule, PipeModule, MatIconModule, MatButtonModule, MatTooltipModule],
	templateUrl: './notam-list.component.html',
	styleUrls: ['./notam-list.component.scss'],
})
export class NotamListComponent implements OnChanges {
	@Input() assessment: NotamAssessmentFieldsFragment | null = null;
	@Input() single: NotamInfoFieldsFragment | null = null;
	/** Optional flight date — far-future flights get the "NOTAMs are published close to the date" hint. */
	@Input() flightDate: string | null = null;
	/** Data-refresh timestamp for `single` mode (in assessment mode it comes from the assessment). */
	@Input() updatedAt: string | null = null;

	sortedNotams: NotamInfoFieldsFragment[] = [];
	wideAreaNotams: NotamInfoFieldsFragment[] = [];
	wideAreaExpanded = false;
	expandedTextIds = new Set<string>();

	ngOnChanges(): void {
		const notams = this.assessment?.notams ?? [];
		// RED before AMBER, closest first within a severity
		this.sortedNotams = [...notams].sort((a, b) => {
			if (a.severity !== b.severity) {
				return a.severity === NotamSeverity.Red ? -1 : 1;
			}
			return (a.distanceKm ?? Number.MAX_VALUE) - (b.distanceKm ?? Number.MAX_VALUE);
		});
		this.wideAreaNotams = this.assessment?.wideAreaNotams ?? [];
	}

	get isEmpty(): boolean {
		return this.sortedNotams.length === 0 && this.wideAreaNotams.length === 0;
	}

	// NOTAMs are typically published only days ahead — tell the operator to re-check (spec edge case)
	get beyondNotamHorizon(): boolean {
		if (!this.flightDate) return false;
		return dayjs(this.flightDate).diff(dayjs(), 'day') > 5;
	}

	toggleText(id: string): void {
		if (this.expandedTextIds.has(id)) {
			this.expandedTextIds.delete(id);
		} else {
			this.expandedTextIds.add(id);
		}
	}

	severityClass(notam: NotamInfoFieldsFragment): string {
		return notam.severity === NotamSeverity.Red ? 'notam-red' : 'notam-amber';
	}

	periodLabel(notam: NotamInfoFieldsFragment): string {
		const from = notam.validFrom ? dayjs(notam.validFrom).format('DD MMM YYYY HH:mm') : '?';
		const to = notam.validTo ? dayjs(notam.validTo).format('DD MMM YYYY HH:mm') : 'PERM';
		return `${from} – ${to}`;
	}

	altitudeBand(notam: NotamInfoFieldsFragment): string {
		const lower = !notam.lowerFt ? 'SFC' : `${notam.lowerFt} ft`;
		const upper = notam.upperFt == null ? 'UNL' : `${notam.upperFt} ft`;
		return `${lower} – ${upper}`;
	}

	distanceLabel(notam: NotamInfoFieldsFragment): string {
		if (notam.distanceKm == null) return '';
		const rounded = notam.distanceKm < 10 ? Math.round(notam.distanceKm * 10) / 10 : Math.round(notam.distanceKm);
		let compass = '';
		if (notam.bearingDeg != null) {
			compass = ' ' + COMPASS_POINTS[Math.round((((notam.bearingDeg % 360) + 360) % 360) / 45) % 8];
		}
		return `${rounded} km${compass}`;
	}

	freshnessLabel(): string {
		const stamp = this.assessment?.updatedAt ?? this.updatedAt;
		if (!stamp) return '';
		return dayjs(stamp).format('DD MMM YYYY HH:mm');
	}
}
